import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, ArrowRight } from 'lucide-react';
import Header from '../component/Header';
import Footer from '../component/Footer';
import { signup } from '../services/authService';
import { useAuth } from '../context/AuthContext';
import { getDashboard } from '../services/userService';
import { hasActiveSubscriptionStatus } from '../utils/subscription';

const Register: React.FC = () => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const navigate = useNavigate();
  const { loginUser, setSubscriptionStatus } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedFullName = `${trimmedFirstName} ${trimmedLastName}`.trim();
    const trimmedEmail = email.trim();

    if (!trimmedFirstName || !trimmedLastName || !trimmedEmail || !password.trim() || !confirmPassword.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    if (!emailPattern.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const response = await signup({
        name: trimmedFullName,
        email: trimmedEmail,
        password,
        user_meta: {
          first_name: trimmedFirstName,
          last_name: trimmedLastName,
        },
      });
      if (response.data?.token) {
        loginUser(response.data.token, {
          ...response.data.user,
          name: response.data.user.name || trimmedFullName,
          email: response.data.user.email || trimmedEmail,
          user_meta: {
            ...response.data.user.user_meta,
            first_name: trimmedFirstName,
            last_name: trimmedLastName,
          },
        });
        try {
          const dashboardResponse = await getDashboard(response.data.token);
          const hasActiveSubscription = hasActiveSubscriptionStatus(dashboardResponse.data.subscriptions);
          setSubscriptionStatus(hasActiveSubscription);
          navigate(hasActiveSubscription ? '/account/' : '/pricing/');
        } catch {
          setSubscriptionStatus(false);
          navigate('/pricing/');
        }
      } else {
        setSuccess(response.message || 'Account created! Please log in.');
        setTimeout(() => navigate('/login/'), 2000);
      }
    } catch (err: any) {
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <main className="min-h-screen flex items-center justify-center bg-white relative overflow-hidden font-sans px-6 py-16">
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        ></div>

        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-[#f59e0b]/10 rounded-full blur-[120px] pointer-events-none"></div>

        <section className="relative z-10 w-full max-w-md">
          <div className="bg-white border border-zinc-100 rounded-[2.5rem] shadow-[0_30px_60px_rgba(0,0,0,0.05)] p-8 md:p-10">
            <div className="flex flex-col items-center mb-8">
             
              <h1 className="text-3xl font-black text-zinc-950 tracking-tighter">Create Account</h1>
              <p className="text-zinc-500 text-sm font-medium mt-1">Start learning with your personal dashboard</p>
            </div>

            {error && (
              <div className="mb-2 px-4 py-3 rounded-2xl bg-red-50 border border-red-200 text-red-600 text-sm font-medium">
                {error}
              </div>
            )}
            {success && (
              <div className="mb-2 px-4 py-3 rounded-2xl bg-amber-50 border border-amber-200 text-amber-700 text-sm font-medium">
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="relative group">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2 ml-1 block">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 group-focus-within:text-[#F59E0B]transition-colors" />
                    <input
                      type="text"
                      placeholder="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 rounded-2xl bg-zinc-50 border border-zinc-200 text-zinc-900 text-sm focus:outline-none focus:border-[#f59e0b] focus:ring-0 focus:ring-[#F59E0B] transition-all"
                      required
                    />
                  </div>
                </div>

                <div className="relative group">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2 ml-1 block">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 group-focus-within:text-[#F59E0B]transition-colors" />
                    <input
                      type="text"
                      placeholder="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 rounded-2xl bg-zinc-50 border border-zinc-200 text-zinc-900 text-sm focus:outline-none focus:border-[#f59e0b] focus:ring-0 focus:ring-[#F59E0B] transition-all"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="relative group">
                <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2 ml-1 block">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 group-focus-within:text-[#F59E0B]transition-colors" />
                  <input
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 rounded-2xl bg-zinc-50 border border-zinc-200 text-zinc-900 text-sm focus:outline-none focus:border-[#f59e0b] focus:ring-0 focus:ring-[#F59E0B] transition-all"
                    required
                  />
                </div>
              </div>

              <div className="relative group">
                <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2 ml-1 block">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 group-focus-within:text-[#F59E0B]transition-colors" />
                  <input
                    type="password"
                    placeholder="Create a strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 rounded-2xl bg-zinc-50 border border-zinc-200 text-zinc-900 text-sm focus:outline-none focus:border-[#f59e0b] focus:ring-0 focus:ring-[#F59E0B] transition-all"
                    minLength={8}
                    required
                  />
                </div>
              </div>

              <div className="relative group">
                <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2 ml-1 block">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 group-focus-within:text-[#F59E0B]transition-colors" />
                  <input
                    type="password"
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 rounded-2xl bg-zinc-50 border border-zinc-200 text-zinc-900 text-sm focus:outline-none focus:border-[#f59e0b] focus:ring-0 focus:ring-[#F59E0B] transition-all"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#09090B] text-white border border-zinc-300 px-8 py-4 rounded-xl font-bold text-lg text-center hover:bg-white hover:text-zinc-950 hover:border-[#09090B] transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating Account...' : (<>Sign Up <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>

            <div className="mt-8 text-center border-t border-zinc-50 pt-8">
              <p className="text-zinc-400 text-sm font-medium">
                Already have an account?{' '}
                <Link to="/login/" className="hover:underline text-zinc-950 font-black  transition-colors">
                  Login
                </Link>
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default Register;
